import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../../utils/baseQuery";
import { components, operations } from "../../types/api";

// Tipos generados desde el swagger del backend (yarn generate:types:local).
// No se escriben a mano: el contrato lo define TSOA.
export type ClientAccountView = components["schemas"]["ClientAccountView"];
export type ClientBrandView = components["schemas"]["ClientBrandView"];
export type ClientAccountType = components["schemas"]["ClientAccountType"];
/** Columnas por las que el listado se puede ordenar. */
export type ClientAccountSortBy = components["schemas"]["ClientAccountSortBy"];
export type OrganizationType = components["schemas"]["OrganizationType"];
export type CompanyLinkOption = components["schemas"]["CompanyLinkOption"];
export type CompanyRegistryLink = components["schemas"]["CompanyRegistryLink"];

type AccountsResponse =
  components["schemas"]["ApiResponse_PaginatedClientAccounts_"];
type AccountResponse = components["schemas"]["ApiResponse_ClientAccountView_"];
type CountriesResponse =
  components["schemas"]["ApiResponse__code-string--name-string_-Array_"];
type CompanySearchResponse =
  components["schemas"]["ApiResponse_CompanyLinkOption-Array_"];
type CompanyLinkResponse =
  components["schemas"]["ApiResponse_CompanyRegistryLink_"];
type OrganizationTypesResponse =
  components["schemas"]["ApiResponse__id-string--organizationTypes-OrganizationType-Array__"];

export type CreateClientAccountRequest =
  components["schemas"]["CreateClientAccountRequest"];
export type UpdateClientAccountRequest =
  components["schemas"]["UpdateClientAccountRequest"];
export type CreateClientBrandRequest =
  components["schemas"]["CreateClientBrandRequest"];
export type UpdateClientBrandRequest =
  components["schemas"]["UpdateClientBrandRequest"];

type ListParams = operations["GetAccounts"]["parameters"]["query"];

export const clientRegistryApi = createApi({
  reducerPath: "clientRegistryApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["ClientAccounts", "Countries"],
  endpoints: (builder) => ({
    // GET /api/client-registry/countries — lista gestionable, no ISO estricto:
    // "3" es la marca general sin país y también sale de aquí.
    getCountries: builder.query<CountriesResponse, void>({
      query: () => "/client-registry/countries",
      providesTags: ["Countries"],
    }),

    // GET /api/client-registry/accounts — el listado que estaba en la hoja de cálculo.
    listClientAccounts: builder.query<AccountsResponse, ListParams | void>({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.page) search.set("page", String(params.page));
        if (params?.pageSize) search.set("pageSize", String(params.pageSize));
        if (params?.businessUnit)
          search.set("businessUnit", params.businessUnit);
        if (params?.type) search.set("type", params.type);
        if (params?.active !== undefined)
          search.set("active", String(params.active));
        if (params?.search) search.set("search", params.search);
        if (params?.sortBy) search.set("sortBy", params.sortBy);
        // La direccion solo viaja con una columna: sola no significa nada.
        if (params?.sortBy && params?.sortDir)
          search.set("sortDir", params.sortDir);
        const qs = search.toString();
        return `/client-registry/accounts${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["ClientAccounts"],
    }),

    getClientAccount: builder.query<AccountResponse, string>({
      query: (id) => `/client-registry/accounts/${id}`,
      providesTags: ["ClientAccounts"],
    }),

    // POST /api/client-registry/accounts — el número NO va en el body: lo asigna el
    // contador del servidor. El primer cliente nuevo de House Group es el C214.
    createClientAccount: builder.mutation<
      AccountResponse,
      CreateClientAccountRequest
    >({
      query: (body) => ({
        url: "/client-registry/accounts",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ClientAccounts"],
    }),

    // PATCH — el número, la unidad de negocio y el tipo no se pueden cambiar: el número
    // ya está impreso en propuestas emitidas.
    updateClientAccount: builder.mutation<
      AccountResponse,
      { id: string; body: UpdateClientAccountRequest }
    >({
      query: ({ id, body }) => ({
        url: `/client-registry/accounts/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["ClientAccounts"],
    }),

    // POST marcas — devuelve la cuenta completa, no la marca: el número de marca se
    // calcula en el servidor y hay que repintar la tabla entera.
    createClientBrand: builder.mutation<
      AccountResponse,
      { accountId: string; body: CreateClientBrandRequest }
    >({
      query: ({ accountId, body }) => ({
        url: `/client-registry/accounts/${accountId}/brands`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ClientAccounts"],
    }),

    updateClientBrand: builder.mutation<
      AccountResponse,
      { brandId: string; body: UpdateClientBrandRequest }
    >({
      query: ({ brandId, body }) => ({
        url: `/client-registry/brands/${brandId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["ClientAccounts"],
    }),

    // DELETE marca — no recalcula contadores. Los huecos son normales en este registro
    // (la cuenta 105 tiene las marcas 10 y 13), así que reutilizar el número de una
    // marca borrada sería peor que dejar el hueco.
    deleteClientBrand: builder.mutation<AccountResponse, string>({
      query: (brandId) => ({
        url: `/client-registry/brands/${brandId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ClientAccounts"],
    }),

    // GET /companies/search?q= — buscador ligero para el selector de enlace.
    // Aparte del listado de empresas a propósito: ese devuelve la empresa entera con
    // sus billings y sin paginar. Desde 2 caracteres, tope 50.
    searchCompaniesForLink: builder.query<CompanySearchResponse, string>({
      query: (q) =>
        `/client-registry/companies/search?q=${encodeURIComponent(q)}`,
    }),

    // GET /companies/{id}/account — la cuenta del registro de una empresa.
    // Devuelve el enlace con `account: null` cuando la empresa existe pero no está
    // enlazada, que es distinto de un 404 y el panel lo necesita para ofrecer el
    // selector.
    getCompanyRegistryLink: builder.query<CompanyLinkResponse, string>({
      query: (companyId) => `/client-registry/companies/${companyId}/account`,
      providesTags: ["ClientAccounts"],
    }),

    // PATCH /companies/{id}/link — enlazar o desenlazar (con clientAccountId null).
    // El número de marca es lo que desempata las cuentas con varias empresas.
    linkCompanyToAccount: builder.mutation<
      CompanyLinkResponse,
      {
        companyId: string;
        clientAccountId: string | null;
        clientBrandNumber?: number | null;
      }
    >({
      query: ({ companyId, ...body }) => ({
        url: `/client-registry/companies/${companyId}/link`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["ClientAccounts"],
    }),

    // PATCH tipos de organización de una empresa. Multi-select: una empresa puede ser
    // agencia y cliente a la vez.
    setCompanyOrganizationTypes: builder.mutation<
      OrganizationTypesResponse,
      { companyId: string; organizationTypes: OrganizationType[] }
    >({
      query: ({ companyId, organizationTypes }) => ({
        url: `/client-registry/companies/${companyId}/organization-types`,
        method: "PATCH",
        body: { organizationTypes },
      }),
    }),
  }),
});

export const {
  useGetCountriesQuery,
  useListClientAccountsQuery,
  useGetClientAccountQuery,
  useCreateClientAccountMutation,
  useUpdateClientAccountMutation,
  useCreateClientBrandMutation,
  useUpdateClientBrandMutation,
  useDeleteClientBrandMutation,
  useSetCompanyOrganizationTypesMutation,
  useLazySearchCompaniesForLinkQuery,
  useGetCompanyRegistryLinkQuery,
  useLinkCompanyToAccountMutation,
} = clientRegistryApi;
